import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";

const TABS = [
  { k: "inventory", l: "On-hand inventory" },
  { k: "expiring", l: "Expiring soon" },
  { k: "shipments", l: "Shipments by customer" },
  { k: "production", l: "Production by date" },
];

function toCSV(rows) {
  if (!rows || rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
}

function download(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const [tab, setTab] = useState("inventory");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(14);

  const load = async () => {
    setLoading(true);
    try {
      let url = "";
      const params = {};
      if (tab === "inventory") url = "/reports/inventory-on-hand";
      if (tab === "expiring") { url = "/reports/expiring-soon"; params.days = days; }
      if (tab === "shipments") url = "/reports/shipments-by-customer";
      if (tab === "production") url = "/reports/production-by-date";
      const { data } = await api.get(url, { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, days]);

  const renderTable = () => {
    if (loading) return <div className="text-zinc-500 p-8 text-center">Loading…</div>;
    if (rows.length === 0) return <div className="text-zinc-500 p-8 text-center">No data.</div>;

    if (tab === "inventory") {
      return (
        <table className="mj-table" data-testid="report-table">
          <thead><tr><th>Product</th><th>SKU</th><th>Pallets</th><th>Total Qty</th><th>Expiring</th><th>Expired</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="font-semibold">{r.product_name}</td>
                <td className="font-mono-id">{r.product_sku}</td>
                <td className="font-mono-id">{r.pallet_count}</td>
                <td className="font-mono-id">{r.total_quantity}</td>
                <td className="font-mono-id">{r.expiring_soon_count}</td>
                <td className="font-mono-id" style={{ color: r.expired_count > 0 ? "var(--mj-red)" : undefined }}>{r.expired_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (tab === "expiring") {
      return (
        <table className="mj-table" data-testid="report-table">
          <thead><tr><th>Pallet #</th><th>Product</th><th>Lot</th><th>Qty</th><th>Expiry</th><th>Days left</th><th>Customer</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="font-mono-id font-semibold">{r.pallet_number}</td>
                <td>{r.product_name}</td>
                <td className="font-mono-id">{r.lot_number}</td>
                <td className="font-mono-id">{r.quantity}</td>
                <td className="font-mono-id">{r.expiry_date}</td>
                <td className="font-mono-id font-bold" style={{ color: r.days_to_expiry < 0 ? "var(--mj-red)" : r.days_to_expiry <= 7 ? "#8A6D15" : undefined }}>
                  {r.days_to_expiry < 0 ? `${Math.abs(r.days_to_expiry)} d ago` : `${r.days_to_expiry} d`}
                </td>
                <td>{r.customer_name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (tab === "shipments") {
      return (
        <table className="mj-table" data-testid="report-table">
          <thead><tr><th>Customer</th><th>Total Shipments</th><th>Total Pallets</th><th>Shipped</th><th>Scheduled</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="font-semibold">{r.customer_name}</td>
                <td className="font-mono-id">{r.shipments_count}</td>
                <td className="font-mono-id">{r.pallets_count}</td>
                <td className="font-mono-id">{r.shipped_count}</td>
                <td className="font-mono-id">{r.scheduled_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (tab === "production") {
      return (
        <table className="mj-table" data-testid="report-table">
          <thead><tr><th>Date</th><th>Pallets</th><th>Total Quantity</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.date}>
                <td className="font-mono-id font-semibold">{r.date}</td>
                <td className="font-mono-id">{r.pallets}</td>
                <td className="font-mono-id">{r.total_quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
  };

  return (
    <div data-testid="reports-page">
      <PageHeader
        eyebrow="Analytics"
        title="Reports."
        action={
          <button onClick={() => download(`${tab}-report.csv`, toCSV(rows))} className="mj-btn mj-btn-secondary" data-testid="export-csv-btn">
            <Download size={14} /> Export CSV
          </button>
        }
      />
      <div className="p-6 md:p-10 space-y-6">
        <div className="flex flex-wrap gap-2 border-b border-[var(--mj-border)] pb-3" data-testid="reports-tabs">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`mj-btn ${tab === t.k ? "mj-btn-primary" : "mj-btn-ghost"}`}
              data-testid={`report-tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
          {tab === "expiring" && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="data-label">Within</span>
              <select className="mj-input" style={{ width: 120, height: 40 }} value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} data-testid="expiring-days-select">
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
          )}
        </div>
        <div className="mj-card overflow-x-auto">{renderTable()}</div>
      </div>
    </div>
  );
}
